import React from "react";
import {
  BlockLineResultSequential,
  MarkdownBaseParser,
  TokensBlockKey,
  TokensInlineKey,
} from "@citychallenge/common";

export {
  type BlockLineResultSequential,
  type MdFileMap,
} from "@citychallenge/common";

interface InlineMatch {
  match: RegExpExecArray;
  key: TokensInlineKey;
}

export type MarkdownInlineRenderer = (
  values: string[],
  key: string | undefined,
  children: string | React.ReactNode[] | React.ReactNode
) => undefined | React.ReactElement;

export class MarkdownParser extends MarkdownBaseParser {
  public static parse(
    content = "",
    callback: (line: BlockLineResultSequential) => React.ReactNode
  ) {
    if (!content) return [];

    const lines = content.split("\n");

    const results: React.ReactNode[] = [];
    let prevToken: TokensBlockKey | undefined;
    let sequence = 1;

    for (const index in lines) {
      const line = lines[index];

      const parsedLine = MarkdownParser.parseLine(line);

      // Increment sequence if multiple tokens follow each other
      sequence = prevToken == parsedLine.token ? sequence + 1 : 1;

      results.push(
        callback({
          ...parsedLine,
          sequence,
          key: `md-${index}`,
          prevToken,
          totaLines: lines.length,
          currentLine: +index,
        })
      );

      prevToken = parsedLine.token;
    }

    return results;
  }

  public static parseInline(
    id: string,
    content = "",
    getRenderer: (token: TokensInlineKey) => MarkdownInlineRenderer | undefined,
    renderLowest: (key: string, text: string) => React.ReactNode
  ): React.ReactNode[] {
    let current: InlineMatch | null = null;

    // Find lowest matching token
    for (const [_key, token] of MarkdownParser.TokensInlineList) {
      const key = _key as TokensInlineKey;
      const match = token.exec(content);
      if (!match) continue;

      const { index } = match;

      if (!current) current = { match, key };
      if (index < current.match.index) current = { match, key };
    }

    if (current) {
      const results = current.match.splice(1, current.match.length - 1);

      const fn = getRenderer(current.key);
      if (!fn) return [];

      const component = fn(
        results,
        `${id}-${current.key}`,
        this.parseInline(`${id}-c`, results[0], getRenderer, renderLowest)
      );

      const sep = current.match[0];
      const [first, ...rest] = content.split(sep);

      // Fix for content with multiple of the same inline tokens. This will result
      // in more than two values in the array. Therefore we should join array items
      // 1 to n into array element 1.
      const parts = [first, rest.join(sep)];

      return [
        ...this.parseInline(`${id}-l`, parts[0], getRenderer, renderLowest),
        component,
        ...this.parseInline(`${id}-r`, parts[1], getRenderer, renderLowest),
      ];
    } else {
      return [renderLowest(`${id}-i`, MarkdownParser.unescape(content))];
    }
  }
}
